import { useMemo, useState } from 'react'
import { Inbox } from 'lucide-react'
import { useSearchParams } from 'wouter'
import {
  Alert,
  Button,
  EmptyState,
  Drawer,
  SearchInput,
  SELECT_ALL_VALUE,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  StateCount,
  fromSelectValue,
  FilterBar,
  PageHeader,
} from '@ideon-api/ideon-ui'
import {
  accountCount,
  filterCarriers,
  hasWildcardGrant,
  issuerIdCount,
  parseCarrierFilters,
  productLineOptions,
  stateOptions,
  summarize,
  type CarrierEntry,
} from '../lib/privileged-carriers'
import { CarrierName } from '../components/CarrierName'
import { MARKETS, lineLabel } from '../lib/product-lines'
import { useExportPrivilegedCarriers, usePrivilegedCarriers } from '../lib/use-privileged-carriers'
import { CarrierDetail } from './privileged-carriers/CarrierDetail'
import { CarrierFlags } from './privileged-carriers/CarrierFlags'

const GRID = 'grid gap-x-4 gap-y-1 sm:grid-cols-[minmax(0,1fr)_90px_90px_90px_minmax(0,1fr)] sm:items-center'

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

const Count = ({ n }: { n: number }) => <span>{n || '—'}</span>

function ListHeader() {
  return (
    <div className={`${GRID} bg-ideon-gray-10 px-4 py-2 text-[13px] font-semibold max-sm:hidden border-b border-ideon-gray-40`}>
      <span>Carrier</span>
      <span>States</span>
      <span>Issuer IDs</span>
      <span>Accounts</span>
      <span>Flags</span>
    </div>
  )
}

const lineFor = (entry: CarrierEntry) => lineLabel(entry.productLine)
const isCaseVariant = (entry: CarrierEntry) => entry.flags.includes('case_variant_duplicate')

function CarrierRow({ entry, onOpen }: { entry: CarrierEntry; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`${GRID} w-full px-4 py-3 text-left text-sm hover:bg-ideon-gray-10`}
    >
      <span className="flex flex-col items-start gap-0.5">
        <span className="font-semibold">
          <CarrierName slug={entry.carrier} raw={isCaseVariant(entry)} />
        </span>
        <span className="text-sm font-normal text-ideon-gray-70">{lineFor(entry)}</span>
      </span>
      <span>{entry.states.length ? <StateCount states={entry.states.length} /> : '—'}</span>
      <Count n={issuerIdCount(entry)} />
      {hasWildcardGrant(entry) ? <span>All</span> : <Count n={accountCount(entry)} />}
      <CarrierFlags flags={entry.flags} malformedIssuerIds={entry.malformedIssuerIds} />
    </button>
  )
}

function CarrierDrawer({ entry, isOpen, onClose }: { entry: CarrierEntry; isOpen: boolean; onClose: () => void }) {
  return (
    <Drawer.Root isOpen={isOpen} onClose={onClose} size="lg" testIdPrefix="carrier-drawer">
      <Drawer.Header className="mb-4 border-b border-ideon-gray-40 pb-4">
        <div className="flex min-w-0 flex-col gap-1">
          <Drawer.Title className="text-lg leading-7">
            <CarrierName slug={entry.carrier} raw={isCaseVariant(entry)} />
          </Drawer.Title>
          <span className="text-sm text-ideon-gray-70">{lineFor(entry)}</span>
          <CarrierFlags flags={entry.flags} malformedIssuerIds={entry.malformedIssuerIds} />
        </div>
        <Drawer.CloseButton />
      </Drawer.Header>
      <Drawer.Body>
        <CarrierDetail entry={entry} />
      </Drawer.Body>
    </Drawer.Root>
  )
}

export function PrivilegedCarriersPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const state = usePrivilegedCarriers()
  const { exportCsv, exporting } = useExportPrivilegedCarriers()
  // The entry stays set after close so the drawer can run its close transition.
  const [openEntry, setOpenEntry] = useState<CarrierEntry | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const parsed = parseCarrierFilters(searchParams)
  const all = useMemo(() => (state.status === 'ready' ? state.data : []), [state])
  const productLines = useMemo(() => productLineOptions(all), [all])
  const states = useMemo(() => stateOptions(all), [all])
  // A URL value with no matching option is dropped, so the list never filters on what the controls cannot show.
  const productLine = productLines.find((o) => o.value.toLowerCase() === parsed.productLine?.toLowerCase())?.value
  const stateCode = states.find((c) => c === parsed.state)
  const filters = { ...parsed, productLine, state: stateCode }
  const { q, market } = filters
  const rows = useMemo(
    () => filterCarriers(all, { q, market, productLine, state: stateCode }),
    [all, q, market, productLine, stateCode],
  )
  const summary = useMemo(() => summarize(rows), [rows])

  const write = (key: string, value: string | undefined) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value) next.set(key, value)
        else next.delete(key)
        return next
      },
      { replace: true }
    )

  return (
    <>
      <PageHeader title="Privileged carriers" subtitle="Carriers whose plans need an explicit grant." />

      <div className="flex flex-col gap-6">
        <FilterBar label="Carrier filters">
          <div className="w-full sm:w-[360px]">
            <SearchInput
              value={filters.q ?? ''}
              onChange={(value) => write('q', value)}
              placeholder="Search carrier"
            />
          </div>
          <Select value={productLine ?? SELECT_ALL_VALUE} onValueChange={(v) => write('product_line', fromSelectValue(v))}>
            <SelectTrigger aria-label="Product line" className="w-full sm:w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SELECT_ALL_VALUE}>All product lines</SelectItem>
              {productLines.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.market ?? SELECT_ALL_VALUE} onValueChange={(v) => write('market', fromSelectValue(v))}>
            <SelectTrigger aria-label="Market" className="w-full sm:w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SELECT_ALL_VALUE}>All markets</SelectItem>
              {MARKETS.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.state ?? SELECT_ALL_VALUE} onValueChange={(v) => write('state', fromSelectValue(v))}>
            <SelectTrigger aria-label="State" className="w-full sm:w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SELECT_ALL_VALUE}>All states</SelectItem>
              {states.map((code) => (
                <SelectItem key={code} value={code}>
                  {code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {state.status === 'ready' && (
            <span className="text-sm text-ideon-gray-70 sm:ml-auto">
              {`${plural(summary.definitions, 'definition')} · ${plural(summary.carriers, 'carrier')} · ${summary.flagged} flagged`}
            </span>
          )}
          {/* The endpoint exports the whole catalog, never the filtered list (PD-31061 AC, AR D13). */}
          <Button variant="outline" disabled={exporting} onClick={() => exportCsv()}>
            Export CSV (all carriers)
          </Button>
        </FilterBar>

        {state.status === 'loading' && (
          <div className="overflow-hidden rounded-lg border border-ideon-gray-40 bg-white">
            <ListHeader />
            <div className="divide-y divide-ideon-gray-40">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="px-4 py-3">
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          </div>
        )}
        {state.status === 'error' && (
          <Alert variant="error" action={<Button onClick={state.reload}>Retry</Button>}>
            Failed to load: {state.message}
          </Alert>
        )}
        {state.status === 'ready' && rows.length === 0 && (
          <EmptyState
            className="bg-white"
            icon={<Inbox className="h-10 w-10" />}
            heading={all.length === 0 ? 'No carrier definitions yet' : 'No carriers match these filters'}
            action={
              all.length > 0 ? (
                <Button
                  variant="text"
                  onClick={() => setSearchParams((prev) => {
                    const next = new URLSearchParams(prev)
                    for (const key of ['q', 'product_line', 'market', 'state']) next.delete(key)
                    return next
                  }, { replace: true })}
                >
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        )}
        {state.status === 'ready' && rows.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-ideon-gray-40 bg-white">
            <ListHeader />
            <div className="divide-y divide-ideon-gray-40">
            {rows.map((entry) => (
              <CarrierRow
                key={`${entry.productLine}:${entry.carrier}`}
                entry={entry}
                onOpen={() => {
                  setOpenEntry(entry)
                  setDrawerOpen(true)
                }}
              />
            ))}
            </div>
          </div>
        )}
        {openEntry && <CarrierDrawer entry={openEntry} isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />}
      </div>
    </>
  )
}
